import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { AnyAction, Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';
import { COUNTRY_CODE } from 'myParkdUtils/enums';
import { getTranslatedSupportedCountryLabels } from 'myParkdUtils/utils';
import { usePrevious } from 'myParkdUtils/customHooks';
import { getErrorMessageKey } from 'myParkdUtils/errorRendering';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import * as companyActions from 'features/company/companyActions';
import * as validation from 'myParkdUtils/validationHandling';
import GlobalErrorBoundary from 'components/GlobalErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import SubmitButton from 'components/SubmitButton';
import ReactFlagsSelect from 'react-flags-select';
import { formatMessage } from 'i18n/CurrentLocale';
import { ReduxState } from 'types';
import { FormSource } from 'features/company/companyDetails';


type CompanyValidation = {
    companyName: validation.ValidationError,
    vat: validation.ValidationError,
    street: validation.ValidationError,
    houseNumber: validation.ValidationError,
    city: validation.ValidationError,
    postalCode: validation.ValidationError,
    invoiceEmail: validation.ValidationError,
}


const CompanySettingsForm = (props: Props) => {
    const [companyDetails, setCompanyDetails] = useState({
        name: '',
        vat: '',
        street: '',
        houseNumber: '',
        city: '',
        postalCode: '',
        countryCode: '',
        invoiceEmail: '',
    });
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);

    const originalCompanyDetails = props.companyDetails?.companyDetails;
    const companyGetStatus = props.companyDetails?.companyGetStatus;
    const companyUpdateStatus = props.companyDetails?.companyUpdateStatus;
    const previousCompanyUpdateStatus = usePrevious(companyUpdateStatus);

    // todo: show error on company get

    useEffect(() => {
        props.getCompanyDetails();
    }, []);

    useEffect(() => {
        if (companyGetStatus.succeeded) {
            setInitialStateValues();
        }
    }, [companyGetStatus]);

    useEffect(() => {
        if (previousCompanyUpdateStatus && previousCompanyUpdateStatus?.loading && companyUpdateStatus.formSource === FormSource.UPDATE_COMPANY_DETAILS &&
            (companyUpdateStatus.succeeded || companyUpdateStatus.error)
        ) {
            setFormSubmitted(true);
        }
    }, [companyUpdateStatus]);

    const setInitialStateValues = () => {
        setCompanyDetails({
            name: originalCompanyDetails?.name || '',
            vat: originalCompanyDetails?.vat || '',
            street: originalCompanyDetails?.address?.street || '',
            houseNumber: originalCompanyDetails?.address?.number || '',
            city: originalCompanyDetails?.address?.city || '',
            postalCode: originalCompanyDetails?.address?.postalCode || '',
            countryCode: originalCompanyDetails?.address?.countryCode || '',
            invoiceEmail: originalCompanyDetails?.email || '',
        });
    };

    const handleUserInput = (e: ChangeEvent<HTMLInputElement>) => {
        setCompanyDetails({
            ...companyDetails,
            [e.target.name]: e.target.value,
        });
    };

    const handleVatInput = (e: ChangeEvent<HTMLInputElement>) => {
        setCompanyDetails({
            ...companyDetails,
            vat: e.target.value.toUpperCase(),
        });
    };

    const onSelectFlag = (countryCode: any) => {
        setCompanyDetails({
            ...companyDetails,
            countryCode: countryCode,
        });
    };

    const getValidationErrors = () => {
        let errors = {} as CompanyValidation;
        errors.companyName = validation.checkIfNotNull(companyDetails.name);
        errors.vat = validation.checkIfNotNull(companyDetails.vat);
        errors.street = validation.checkIfNotNull(companyDetails.street);
        errors.houseNumber = validation.checkIfNotNull(companyDetails.houseNumber);
        errors.city = validation.checkIfNotNull(companyDetails.city);
        errors.postalCode = validation.checkIfNotNull(companyDetails.postalCode);
        errors.invoiceEmail = validation.checkIfValidEmailAddress(companyDetails.invoiceEmail);
        return errors;
    };

    const handleCompanySettingsFormSubmit = (e: FormEvent) => {
        e.preventDefault();
        const validationSuccessful = validation.validate(getValidationErrors());
        if (validationSuccessful) {
            props.updateCompanyDetails({
                name: companyDetails.name,
                vat: companyDetails.vat,
                address: {
                    street: companyDetails.street,
                    number: companyDetails.houseNumber,
                    city: companyDetails.city,
                    postalCode: companyDetails.postalCode,
                    countryCode: companyDetails.countryCode,
                },
                email: companyDetails.invoiceEmail,
                workingHoursStart: originalCompanyDetails.workingHoursStart,
                workingHoursEnd: originalCompanyDetails.workingHoursEnd
            }, FormSource.UPDATE_COMPANY_DETAILS);
        } else {
            setShowValidationErrors(true);
        }
    };

    const err = getValidationErrors();
    const validationSuccessful = validation.validate(err);

    return (
        <div className="radius-rectangle settings-box">
            <div className="settings-box-header">
                <h3 className="bold-title">
                    <FontAwesomeIcon icon={faBuilding} className="icon-before" />
                    <FormattedMessage id="SETTINGS_COMPANY_DETAILS" />
                </h3>
            </div>
            <GlobalErrorBoundary className="eb-red-background eb-rounded-corners-bottom">
                <div className="settings-box-content">
                    {companyGetStatus.succeeded && (
                        <form
                            id="companySettingsForm"
                            onSubmit={handleCompanySettingsFormSubmit}
                            noValidate
                            className={
                                (validationSuccessful ? 'validation-successful ' : '') +
                                (showValidationErrors ? 'show-validation-messages' : 'hide-validation-messages')
                            }
                        >
                            <fieldset className="full-width-form">
                                <div className="row-7">
                                    <div className="col-6">
                                        <label htmlFor="settings-company-name">
                                            <span>
                                                <FormattedMessage id="COMPANY_NAME" />
                                            </span>
                                        </label>
                                        <input
                                            id="settings-company-name"
                                            type="text"
                                            className={'text-field' + (err.companyName ? ' error' : ' valid')}
                                            name="name"
                                            value={companyDetails.name}
                                            onChange={handleUserInput}
                                            disabled
                                        />
                                        <ErrorMessage msg={err.companyName} />
                                    </div>
                                    <div className="col-6">
                                        <label htmlFor="settings-vat">
                                            <span>
                                                <FormattedMessage id="VAT_NUMBER" />
                                            </span>
                                        </label>
                                        <input
                                            id="settings-vat"
                                            type="text"
                                            className={'text-field' + (err.vat ? ' error' : ' valid')}
                                            name="vat"
                                            value={companyDetails.vat}
                                            onChange={handleVatInput}
                                            disabled
                                        />
                                        <ErrorMessage msg={err.vat} />
                                    </div>
                                </div>

                                <div className="row-7">
                                    <div className="col-6">
                                        <label htmlFor="settings-street">
                                            <span>
                                                <FormattedMessage id="STREET" />
                                            </span>
                                        </label>
                                        <input
                                            id="settings-street"
                                            type="text"
                                            className={'text-field' + (err.street ? ' error' : ' valid')}
                                            name="street"
                                            value={companyDetails.street}
                                            onChange={handleUserInput}
                                            disabled
                                        />
                                        <ErrorMessage msg={err.street} />
                                    </div>
                                    <div className="col-6">
                                        <label htmlFor="settings-house-number">
                                            <span>
                                                <FormattedMessage id="HOUSE_NUMBER" />
                                            </span>
                                        </label>
                                        <input
                                            id="settings-house-number"
                                            type="text"
                                            className={'text-field' + (err.houseNumber ? ' error' : ' valid')}
                                            name="houseNumber"
                                            value={companyDetails.houseNumber}
                                            onChange={handleUserInput}
                                            disabled
                                        />
                                        <ErrorMessage msg={err.houseNumber} />
                                    </div>
                                </div>

                                <div className="row-7">
                                    <div className="col-6">
                                        <label htmlFor="settings-city">
                                            <span>
                                                <FormattedMessage id="CITY" />
                                            </span>
                                        </label>
                                        <input
                                            id="settings-city"
                                            type="text"
                                            className={'text-field' + (err.city ? ' error' : ' valid')}
                                            name="city"
                                            value={companyDetails.city}
                                            onChange={handleUserInput}
                                            disabled
                                        />
                                        <ErrorMessage msg={err.city} />
                                    </div>
                                    <div className="col-6">
                                        <label htmlFor="settings-postal-code">
                                            <span>
                                                <FormattedMessage id="POSTAL_CODE" />
                                            </span>
                                        </label>
                                        <input
                                            id="settings-postal-code"
                                            type="text"
                                            className={'text-field' + (err.postalCode ? ' error' : ' valid')}
                                            name="postalCode"
                                            value={companyDetails.postalCode}
                                            onChange={handleUserInput}
                                            disabled
                                        />
                                        <ErrorMessage msg={err.postalCode} />
                                    </div>
                                </div>

                                <div className="row-7" style={{ marginBottom: '20px' }}>
                                    {companyDetails.countryCode && (
                                        <div className="col-6">
                                            <label>
                                                <span>
                                                    <FormattedMessage id="COUNTRY" />
                                                </span>
                                            </label>
                                            <ReactFlagsSelect
                                                selected={companyDetails.countryCode}
                                                countries={Object.values(COUNTRY_CODE)}
                                                className="country-flags text-field react-flags-select-inside-text-field"
                                                name="countryCode"
                                                onSelect={onSelectFlag}
                                                required
                                                customLabels={getTranslatedSupportedCountryLabels()}
                                                selectedSize={18}
                                                optionsSize={18}
                                                disabled
                                            />
                                            <ErrorMessage msg={err.countryCode} />
                                        </div>
                                    )}

                                    <div className="col-6">
                                        <label htmlFor="settings-invoice-email">
                                            <span>
                                                <FormattedMessage id="INVOICE_EMAIL" />
                                            </span>
                                        </label>
                                        <input
                                            id="settings-invoice-email"
                                            type="email"
                                            className={'text-field' + (err.invoiceEmail ? ' error' : ' valid')}
                                            name="invoiceEmail"
                                            value={companyDetails.invoiceEmail}
                                            onChange={handleUserInput}
                                            disabled
                                        />
                                        <ErrorMessage msg={err.invoiceEmail} />
                                    </div>
                                </div>

                                {formSubmitted && (
                                    <React.Fragment>
                                        {companyUpdateStatus.error && (
                                            <div className="error-box">
                                                <FormattedMessage id={getErrorMessageKey(companyUpdateStatus.error)} />
                                            </div>
                                        )}
                                        {companyUpdateStatus.succeeded && (
                                            <div className="success-box">
                                                <FormattedMessage id="SETTINGS_PROFILE_UPDATE_SUCCESS" />
                                            </div>
                                        )}
                                    </React.Fragment>
                                )}

                                {/*
                                <SubmitButton buttonText={formatMessage({ id: 'SETTINGS_SAVE_COMPANY_DETAILS' })} saving={companyUpdateStatus.loading} />
                                */}
                            </fieldset>
                        </form>
                    )}

                    <div style={{ marginTop: '10px' }}>
                        <p>
                            <FormattedMessage
                                id="SETTINGS_REQUEST_UPDATE_BY_MAIL"
                                values={{
                                    a: chunks => (
                                        <a
                                            className="text-link"
                                            href="mailto:finance@parkd.com"
                                        >
                                            {chunks}
                                        </a>
                                    )
                                }}
                            />
                        </p>
                    </div>

                    {companyGetStatus.error && (
                        <div className="error-box">
                            <FormattedMessage id="ERROR_SOMETHING_WENT_WRONG" />
                        </div>
                    )}
                </div>
            </GlobalErrorBoundary >
        </div >
    );
};


type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;


function mapStateToProps(state: ReduxState) {
    return {
        companyDetails: state.companyDetails,
    };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return bindActionCreators(
        {
            getCompanyDetails: companyActions.getCompanyDetails,
            updateCompanyDetails: companyActions.updateCompanyDetails,
        },
        dispatch
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(CompanySettingsForm);